// @figma file=tbd node=tbd name="LanguageDropdown" set="tbd" verified=tbd
import * as React from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from './dropdown-menu';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface LanguageDropdownProps {
  trigger: React.ReactNode;
  defaultOpen?: boolean;
  align?: 'start' | 'center' | 'end';
  /** Controlled selected locale code (e.g. 'en', 'fr'). */
  value?: string;
  /** Called when the user selects a new locale. */
  onValueChange?: (code: string) => void;
  /** Override the displayed options. Defaults to the built-in EN/FR/ES/PT list. */
  options?: { code: string; label: string }[];
}

// ─── Default options (kept for backward-compat when no options prop is passed) ─

const DEFAULT_OPTIONS = [
  { code: 'en', label: 'English' },
  { code: 'fr', label: 'Français' },
  { code: 'es', label: 'Español' },
  { code: 'pt', label: 'Português' },
];

// ─── LanguageDropdown ─────────────────────────────────────────────────────────

const LanguageDropdown = React.forwardRef<HTMLDivElement, LanguageDropdownProps>(
  ({ defaultOpen, align, trigger, value, onValueChange, options }, ref) => {
    const [internalValue, setInternalValue] = React.useState(value ?? 'en');
    const displayOptions = options ?? DEFAULT_OPTIONS;

    const handleChange = (code: string) => {
      setInternalValue(code);
      onValueChange?.(code);
    };

    const effectiveValue = value ?? internalValue;

    return (
      <DropdownMenu defaultOpen={defaultOpen}>
        <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
        <DropdownMenuContent ref={ref} className="w-50" align={align ?? 'end'}>
          <DropdownMenuRadioGroup value={effectiveValue} onValueChange={handleChange}>
            {displayOptions.map((opt) => (
              <DropdownMenuRadioItem
                key={opt.code}
                value={opt.code}
                className="data-[state=checked]:bg-accent data-[state=checked]:text-accent-foreground pl-2 text-base [&>span]:hidden"
              >
                {opt.label}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  },
);
LanguageDropdown.displayName = 'LanguageDropdown';

export { LanguageDropdown };
